'use client';

import React, { useRef, useEffect, ReactNode } from 'react';
import styled from 'styled-components';
import { theme } from '@/config';
import { SortIcon, DropdownArrowIcon } from '@/components/icons';
import { Button } from '@/components/ui-components/Button/Button';
import { Radio } from '@/components/forms';

// ============================================================================
// Styled Components
// ============================================================================

const SortDropdownWrapper = styled.div`
  position: relative;
  min-width: 120px;
`;

const SortButton = styled(Button)`
  min-width: 120px;
  padding: 6px 8px;
  justify-content: flex-start;
  background-color: ${theme.colors.white};

  &:hover {
    border-color: ${theme.colors.gray[400]};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SortDropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 4px;
  box-shadow: ${theme.shadows.md};
  min-width: 120px;
  width: fit-content;
  z-index: 10000;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  overflow: hidden;
`;

const SortOption = styled.div`
  padding: 14px 8px;
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.normal};
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  &:last-child {
    border-bottom: none;
  }
  border-bottom: 1px solid ${theme.colors.border.primary};

  &:hover {
    background-color: ${theme.colors.gray[50]};
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface DropdownOption<T extends string = string> {
  value: T;
  label: string;
}

interface SortDropdownProps<T extends string = string> {
  value: T;
  options: DropdownOption<T>[];
  onChange: (value: T) => void;
  buttonContent?: ReactNode;
  icon?: ReactNode;
  name?: string;
  style?: React.CSSProperties;
}

// ============================================================================
// Component
// ============================================================================

export const SortDropdown = <T extends string = string>({ 
  value, 
  options, 
  onChange, 
  buttonContent,
  icon,
  name = 'dropdown',
  style,
}: SortDropdownProps<T>) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <SortDropdownWrapper ref={sortDropdownRef}>
      <SortButton
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        type="button"
        variant="outline"
        style={style}
      >
        {icon || <SortIcon width={16} height={16} color="currentColor" />}
        {buttonContent || <span>Sort</span>}
        <div style={{ marginLeft: 'auto' }}>
          <DropdownArrowIcon color={theme.colors.gray[550] || ''} />
        </div>
      </SortButton>
      <SortDropdownMenu $isOpen={isOpen}>
        {options.map((option) => (
          <SortOption 
            key={option.value}
            onClick={() => {
              onChange(option.value as T);
              setIsOpen(false);
            }}
          >
            <Radio
              name={name}
              value={option.value}
              label={option.label}
              checked={value === option.value}
              labelStyle={{ fontSize: '12px' }}
              onChange={() => {
                onChange(option.value as T);
                setIsOpen(false);
              }}
            />
          </SortOption>
        ))}
      </SortDropdownMenu>
    </SortDropdownWrapper>
  );
};

